import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Package,
  Search,
  RefreshCcw,
  ClipboardList,
  Plus,
  X,
  ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { warehouseInventoryService } from "../../../services/warehouseInventory.service";
import { warehouseCatalogService } from "../../../services/warehouseCatalog.service";

const BORDER = "#f2c6cc";

function SelectLike({
  value,
  onChange,
  placeholder,
  options,
  disabled = false,
}) {
  const [open, setOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selected = (options || []).find(
    (o) => String(o.value ?? o) === String(value)
  );

  return (
    <div ref={dropdownRef} className="relative min-w-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={`h-11 w-full rounded-xl border px-4 pr-10 text-left text-[13px] outline-none transition
          ${disabled
            ? "cursor-not-allowed border-[#f2c6cc] bg-slate-50 text-slate-400"
            : "border-[#f2c6cc] bg-white text-slate-600 hover:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
          }`}
      >
        <span className="block truncate">
          {selected ? selected.label ?? selected : placeholder}
        </span>
      </button>

      <ChevronDown
        size={18}
        className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition ${open ? "rotate-180" : ""
          }`}
      />

      {open && !disabled && (
        <div className="absolute left-0 top-full z-[999] mt-2 max-h-64 w-full overflow-y-auto rounded-xl border border-[#f2c6cc] bg-white shadow-[0_10px_30px_rgba(216,106,120,0.15)]">
          <button
            type="button"
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className="w-full truncate px-4 py-3 text-left text-[13px] text-slate-500 transition hover:bg-[#fff4f6] hover:text-[#d86a78]"
          >
            {placeholder}
          </button>

          {(options || []).map((o) => {
            const val = o.value ?? o;
            const label = o.label ?? o;
            const active = String(value) === String(val);

            return (
              <button
                key={val}
                type="button"
                onClick={() => {
                  onChange(val);
                  setOpen(false);
                }}
                title={label}
                className={`w-full truncate px-4 py-3 text-left text-[13px] transition
                  ${active
                    ? "bg-[#d86a78] text-white"
                    : "text-slate-600 hover:bg-[#fff4f6] hover:text-[#d86a78]"
                  }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function normalizeLookupList(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.rows)) return value.rows;
  if (Array.isArray(value?.result)) return value.result;
  return [];
}

function getLookupId(item) {
  return item?.id ?? item?._id ?? item?.value ?? "";
}

function getLookupLabel(item) {
  return item?.name || item?.label || item?.slug || item?.title || getLookupId(item);
}

function getParentId(item) {
  return item?.parentId ?? item?.parent_id ?? item?.categoryId ?? item?.category_id ?? "";
}

function uniqueLookupList(items = []) {
  const seen = new Set();
  return normalizeLookupList(items).filter((item) => {
    const id = String(getLookupId(item) || "");
    const key = id || String(getLookupLabel(item) || "").toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function cleanText(value) {
  return String(value ?? "").trim().toLowerCase();
}

function valuesFromKeys(item = {}, keys = []) {
  return keys
    .flatMap((key) => {
      const value = item?.[key];
      if (value && typeof value === "object") {
        return [value?.id, value?._id, value?.name, value?.label].filter(
          (nested) => nested !== undefined && nested !== null && String(nested).trim() !== "",
        );
      }
      return [value];
    })
    .filter((value) => value !== undefined && value !== null && String(value).trim() !== "")
    .map((value) => String(value));
}

function matchesByIdsOrNames(item, { ids = [], names = [], idKeys = [], nameKeys = [] }) {
  const wantedIds = ids.map((id) => String(id)).filter(Boolean);
  const wantedNames = names.map(cleanText).filter(Boolean);

  if (!wantedIds.length && !wantedNames.length) return true;

  const itemIds = valuesFromKeys(item, idKeys);
  if (itemIds.length && itemIds.some((id) => wantedIds.includes(String(id)))) {
    return true;
  }

  const itemNames = valuesFromKeys(item, nameKeys).map(cleanText);
  if (itemNames.length && itemNames.some((name) => wantedNames.includes(name))) {
    return true;
  }

  return false;
}

function optionLabel(options = [], value) {
  return options.find((option) => String(option.value) === String(value))?.label || "";
}

function firstFilled(item = {}, keys = []) {
  for (const key of keys) {
    const value = item?.[key];
    if (value && typeof value === "object") {
      const nested = value?.id || value?._id || value?.name || value?.label || "";
      if (nested !== undefined && nested !== null && String(nested).trim() !== "") return nested;
      continue;
    }
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
}

function buildCategoryMeta(categories = []) {
  const normalized = uniqueLookupList(categories);
  const byId = new Map();
  normalized.forEach((item) => {
    const id = String(getLookupId(item) || "");
    if (id) byId.set(id, item);
  });
  return { categories: normalized, byId };
}

function categoryPath(categoryId, byId = new Map()) {
  const parts = [];
  const seen = new Set();
  let currentId = String(categoryId || "");

  while (currentId && !seen.has(currentId)) {
    seen.add(currentId);
    const item = byId.get(currentId);
    if (!item) break;
    parts.unshift(getLookupLabel(item));
    currentId = String(getParentId(item) || "");
  }

  return parts.filter(Boolean).join(" > ");
}

function collectCategoryDescendantIds(categories = [], rootId = "") {
  const root = String(rootId || "");
  if (!root) return [];

  const childrenByParent = new Map();
  for (const item of categories) {
    const id = String(getLookupId(item) || "");
    const parentId = String(getParentId(item) || "");
    if (!id) continue;
    if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
    childrenByParent.get(parentId).push(id);
  }

  const result = [];
  const seen = new Set();
  const queue = [root];
  while (queue.length) {
    const id = queue.shift();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
    queue.push(...(childrenByParent.get(id) || []));
  }
  return result;
}

function getItemCategoryId(item = {}) {
  return firstFilled(item, [
    "category_id",
    "categoryId",
    "category",
    "subcategory_id",
    "subCategoryId",
    "subcategoryId",
  ]);
}

function getItemCategoryParentId(item = {}, categoryById = new Map()) {
  const direct = firstFilled(item, [
    "category_parent_id",
    "categoryParentId",
    "parent_category_id",
    "parentCategoryId",
  ]);
  if (direct) return direct;

  const categoryId = String(getItemCategoryId(item) || "");
  const categoryItem = categoryById.get(categoryId);
  return categoryItem ? getParentId(categoryItem) : "";
}

function getUsedCategoryIds(items = [], categoryById = new Map()) {
  const used = new Set();

  items.forEach((item) => {
    const categoryId = String(getItemCategoryId(item) || "");
    const parentId = String(getItemCategoryParentId(item, categoryById) || "");
    if (categoryId) used.add(categoryId);
    if (parentId) used.add(parentId);
  });

  return used;
}

function filterProductBySearch(product, query) {
  const q = cleanText(query);
  if (!q) return true;

  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const variantText = variants
    .map((variant) =>
      [
        variant?.variantName,
        variant?.variantSku,
        variant?.barcode,
        variant?.packLabel,
        variant?.weightValue,
        variant?.weightUnitSymbol,
      ]
        .filter(Boolean)
        .join(" "),
    )
    .join(" ");

  return cleanText([
    product?.name,
    product?.sku,
    product?.barcode,
    product?.category_name,
    product?.categoryName,
    product?.brand_name,
    product?.brandName,
    variantText,
  ].filter(Boolean).join(" ")).includes(q);
}

async function fetchAllAssignedProducts() {
  const limit = 200;
  const maxPages = 50;
  let pageNo = 1;
  let total = 0;
  const all = [];
  const seen = new Set();

  while (pageNo <= maxPages) {
    const res = await warehouseCatalogService.listAssignedProducts({
      page: pageNo,
      limit,
    });

    const items = Array.isArray(res?.items) ? res.items : [];
    total = Number(res?.total || total || items.length || 0);

    items.forEach((item, index) => {
      const key = String(item?.id || `${pageNo}-${index}`);
      if (seen.has(key)) return;
      seen.add(key);
      all.push(item);
    });

    if (!items.length || items.length < limit || (total > 0 && all.length >= total)) {
      break;
    }

    pageNo += 1;
  }

  return { items: all, total: total || all.length };
}

function statusText(error) {
  return error?.message || "Something went wrong";
}

function getImageUrl(item) {
  if (item?.image_url) return item.image_url;

  if (Array.isArray(item?.images) && item.images.length) {
    const first = item.images[0];
    if (typeof first === "string") return first;
    return first?.url || first?.image_url || "";
  }

  return "";
}

function getTimeValue(value) {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function pickBestPrice(prices = []) {
  const list = Array.isArray(prices) ? prices : [];

  if (!list.length) return null;

  const warehousePrices = list.filter((p) => p?.warehouseId);
  const source = warehousePrices.length ? warehousePrices : list;

  return [...source].sort((a, b) => {
    const aActive = a?.active ? 1 : 0;
    const bActive = b?.active ? 1 : 0;
    if (bActive !== aActive) return bActive - aActive;

    const aFrom = getTimeValue(a?.effectiveFrom);
    const bFrom = getTimeValue(b?.effectiveFrom);
    if (bFrom !== aFrom) return bFrom - aFrom;

    const aTo = getTimeValue(a?.effectiveTo);
    const bTo = getTimeValue(b?.effectiveTo);
    if (bTo !== aTo) return bTo - aTo;

    return 0;
  })[0];
}

function getPrimaryPrice(item) {
  const prices = Array.isArray(item?.prices) ? item.prices : [];

  const warehousePrices = prices.filter((p) => p?.warehouseId);
  const selectedPrice = warehousePrices.length
    ? warehousePrices[warehousePrices.length - 1]
    : prices.length
      ? prices[prices.length - 1]
      : null;

  return {
    mrp: selectedPrice?.mrp ?? item?.mrp ?? null,
    sellingPrice:
      selectedPrice?.sellingPrice ??
      selectedPrice?.selling_price ??
      item?.selling_price ??
      item?.sellingPrice ??
      null,
    currency: selectedPrice?.currency || item?.currency || "INR",
  };
}

function getBatchSummary(item) {
  const batches = Array.isArray(item?.batches) ? item.batches : [];

  return {
    totalAvailable: batches.reduce(
      (sum, batch) => sum + Number(batch?.qtyAvailable || 0),
      0,
    ),
  };
}

function getVariantLabel(variant = {}, index = 0) {
  return (
    variant?.label ||
    variant?.variantName ||
    variant?.name ||
    variant?.packLabel ||
    [variant?.weightValue, variant?.weightUnitSymbol]
      .filter(Boolean)
      .join(" ") ||
    `Variant ${index + 1}`
  );
}

function formatCurrency(value, currency = "INR") {
  if (value === null || value === undefined || value === "") return "-";

  const amount = Number(value);
  if (Number.isNaN(amount)) return value;

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

export default function WarehouseProducts() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [, setMeta] = useState({ total: 0, page: 1, limit: 10 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [lookups, setLookups] = useState({
    loading: false,
    error: "",
    categories: [],
    subcategories: [],
    brands: [],
  });

  const [requestOpen, setRequestOpen] = useState(false);
  const [requestItems, setRequestItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLookups((prev) => ({ ...prev, loading: true, error: "" }));

        const [categoriesRes, brandsRes] = await Promise.all([
          warehouseCatalogService.getAllCategories
            ? warehouseCatalogService.getAllCategories()
            : warehouseCatalogService.getCategories(),
          warehouseCatalogService.getBrands(),
        ]);

        if (!alive) return;

        const categoryList = uniqueLookupList(categoriesRes);

        setLookups({
          loading: false,
          error: "",
          categories: categoryList,
          subcategories: categoryList.filter((item) => getParentId(item)),
          brands: uniqueLookupList(brandsRes),
        });
      } catch (err) {
        if (!alive) return;
        setLookups((prev) => ({
          ...prev,
          loading: false,
          error: statusText(err) || "Failed to load filters",
        }));
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await fetchAllAssignedProducts();

      setProducts(Array.isArray(res?.items) ? res.items : []);
      setMeta({
        total: Number(res?.total || res?.items?.length || 0),
        page: 1,
        limit: pageSize,
      });
    } catch (err) {
      setError(statusText(err));
      setProducts([]);
      setMeta({ total: 0, page: 1, limit: pageSize || 10 });
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPage(1);
  }

  function buildRequestItem(product, variant) {
    const variantPrice = pickBestPrice(variant?.prices || []);
    const batch = getBatchSummary(variant);

    return {
      productId: product.id,
      productVariantId: variant?.id || "",
      variants: Array.isArray(product?.variants) ? product.variants : [],
      name: product.name || "Product",
      variantName: getVariantLabel(variant),
      variantLabel: getVariantLabel(variant),
      sku: variant?.variantSku || product?.sku || "",
      barcode: product.barcode || "",
      category: product.category_name || "-",
      brand: product.brand_name || "-",
      uom: product.unit_symbol || product.unit_name || "-",
      mrp: variantPrice?.mrp ?? variant?.mrp ?? product?.mrp ?? null,
      sellingPrice:
        variantPrice?.sellingPrice ??
        variantPrice?.selling_price ??
        variant?.sellingPrice ??
        product?.selling_price ??
        null,
      currency: variantPrice?.currency || product?.currency || "INR",
      image: getImageUrl(product),
      availableQty: batch.totalAvailable || 0,
      qty: 1,
      itemNote: "",
    };
  }

  function goToProductDetails(productId) {
    if (!productId) return;
    navigate(`/admin/dashboard/products/${productId}`);
  }

  function addProductToRequest(product, variant) {
    if (!product?.id || !variant?.id) return;

    setSubmitMessage("");

    setRequestItems((prev) => {
      const exists = prev.find(
        (x) =>
          x.productId === product.id &&
          String(x.productVariantId || "") === String(variant.id || ""),
      );

      if (exists) {
        return prev.map((x) =>
          x.productId === product.id &&
            String(x.productVariantId || "") === String(variant.id || "")
            ? { ...x, qty: Number(x.qty || 0) + 1 }
            : x,
        );
      }

      return [...prev, buildRequestItem(product, variant)];
    });
  }

  function updateItemVariant(productId, oldVariantId, newVariantId) {
    setRequestItems((prev) => {
      const currentItem = prev.find(
        (x) =>
          x.productId === productId &&
          String(x.productVariantId || "") === String(oldVariantId || ""),
      );

      if (!currentItem) return prev;

      const selectedVariant = (currentItem.variants || []).find(
        (variant) => String(variant?.id || "") === String(newVariantId || ""),
      );

      if (!selectedVariant) return prev;

      const duplicateItem = prev.find(
        (x) =>
          x.productId === productId &&
          String(x.productVariantId || "") === String(newVariantId || "") &&
          !(
            x.productId === productId &&
            String(x.productVariantId || "") === String(oldVariantId || "")
          ),
      );

      if (duplicateItem) {
        return prev
          .filter(
            (x) =>
              !(
                x.productId === productId &&
                String(x.productVariantId || "") === String(oldVariantId || "")
              ),
          )
          .map((x) =>
            x.productId === productId &&
              String(x.productVariantId || "") === String(newVariantId || "")
              ? { ...x, qty: Number(x.qty || 0) + Number(currentItem.qty || 0) }
              : x,
          );
      }

      const variantPrice = pickBestPrice(selectedVariant?.prices || []);
      const batch = getBatchSummary(selectedVariant);

      return prev.map((x) =>
        x.productId === productId &&
          String(x.productVariantId || "") === String(oldVariantId || "")
          ? {
            ...x,
            productVariantId: selectedVariant?.id || "",
            variantName: getVariantLabel(selectedVariant),
            variantLabel: getVariantLabel(selectedVariant),
            sku: selectedVariant?.variantSku || x.sku || "",
            mrp: variantPrice?.mrp ?? selectedVariant?.mrp ?? x.mrp ?? null,
            sellingPrice:
              variantPrice?.sellingPrice ??
              variantPrice?.selling_price ??
              selectedVariant?.sellingPrice ??
              x.sellingPrice ??
              null,
            currency: variantPrice?.currency || x.currency || "INR",
            availableQty: batch.totalAvailable || 0,
          }
          : x,
      );
    });
  }

  function updateQty(productId, productVariantId, qty) {
    setRequestItems((prev) =>
      prev.map((x) =>
        x.productId === productId &&
          String(x.productVariantId || "") === String(productVariantId || "")
          ? { ...x, qty: qty === "" ? "" : Math.max(1, Number(qty || 1)) }
          : x,
      ),
    );
  }

  function updateItemNote(productId, productVariantId, value) {
    setRequestItems((prev) =>
      prev.map((x) =>
        x.productId === productId &&
          String(x.productVariantId || "") === String(productVariantId || "")
          ? { ...x, itemNote: value }
          : x,
      ),
    );
  }

  function removeRequestItem(productId, productVariantId) {
    setRequestItems((prev) =>
      prev.filter(
        (x) =>
          !(
            x.productId === productId &&
            String(x.productVariantId || "") === String(productVariantId || "")
          ),
      ),
    );
  }

  async function submitOrderRequest() {
    try {
      setSubmitting(true);
      setSubmitMessage("");

      const invalidItem = requestItems.find(
        (x) => !x.productId || !x.qty || Number(x.qty) <= 0,
      );

      if (invalidItem) {
        throw new Error(
          "Please fill valid quantity for all requested products",
        );
      }

      const cleanItems = requestItems.map((x) => ({
        productId: x.productId,
        productVariantId: x.productVariantId || undefined,
        qty: Number(x.qty),
      }));

      if (!cleanItems.length) {
        throw new Error("Add at least one product before sending request");
      }

      await warehouseInventoryService.createOrder({
        notes: notes.trim() || null,
        items: cleanItems,
      });

      setSubmitMessage("Order request sent successfully");
      setRequestItems([]);
      setNotes("");
      setRequestOpen(false);
    } catch (err) {
      setSubmitMessage(statusText(err));
    } finally {
      setSubmitting(false);
    }
  }

  const filterOptions = useMemo(() => {
    const { categories, byId } = buildCategoryMeta([...(lookups.categories || []), ...(lookups.subcategories || [])]);
    const usedCategoryIds = getUsedCategoryIds(products, byId);

    // Include ancestors of actually-used leaf categories so a parent/department remains
    // selectable even when products live several levels below it.
    const visibleIds = new Set();
    for (const usedId of usedCategoryIds) {
      let currentId = String(usedId || "");
      const seen = new Set();
      while (currentId && !seen.has(currentId)) {
        seen.add(currentId);
        visibleIds.add(currentId);
        const item = byId.get(currentId);
        currentId = String(item ? getParentId(item) : "");
      }
    }

    const categoryOptions = categories
      .filter((item) => !visibleIds.size || visibleIds.has(String(getLookupId(item) || "")))
      .map((item) => ({
        value: getLookupId(item),
        label: categoryPath(getLookupId(item), byId) || getLookupLabel(item),
      }))
      .filter((item) => item.value)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));

    const brandOptions = uniqueLookupList(lookups.brands)
      .map((item) => ({ value: getLookupId(item), label: getLookupLabel(item) }))
      .filter((item) => item.value)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));

    return { category: categoryOptions, brand: brandOptions };
  }, [lookups, products]);

  const filteredProducts = useMemo(() => {
    const { categories, byId } = buildCategoryMeta([...(lookups.categories || []), ...(lookups.subcategories || [])]);
    const categoryIds = collectCategoryDescendantIds(categories, category);
    const categoryNames = categoryIds
      .map((id) => getLookupLabel(byId.get(String(id))))
      .filter(Boolean);

    const brandIds = brand ? [brand] : [];
    const brandNames = brand
      ? [optionLabel(filterOptions.brand, brand)].filter(Boolean)
      : [];

    return products.filter((item) => {
      if (!filterProductBySearch(item, search)) return false;

      const categoryOk = matchesByIdsOrNames(item, {
        ids: categoryIds,
        names: categoryNames,
        idKeys: [
          "category_id",
          "categoryId",
          "category",
          "category_parent_id",
          "categoryParentId",
          "parent_category_id",
          "parentCategoryId",
          "subcategory_id",
          "subCategoryId",
          "subcategoryId",
        ],
        nameKeys: [
          "category_name",
          "categoryName",
          "category_label",
          "categoryLabel",
          "subcategory_name",
          "subCategoryName",
          "subcategoryName",
        ],
      });

      const brandOk = matchesByIdsOrNames(item, {
        ids: brandIds,
        names: brandNames,
        idKeys: ["brand_id", "brandId", "brand"],
        nameKeys: ["brand_name", "brandName", "brand_label", "brandLabel"],
      });

      return categoryOk && brandOk;
    });
  }, [products, search, category, brand, filterOptions, lookups]);

  const visibleProducts = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredProducts.slice(start, start + pageSize);
  }, [filteredProducts, page, pageSize]);

  const total = Number(filteredProducts.length || 0);

  const totalPages = useMemo(() => {
    if (!total || total <= 0) return 1;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  const pageSizeOptions = useMemo(() => {
    const base = [10, 25, 50, 100, 250, 500];

    let opts = base.filter((n) => total > 0 && n <= total);

    if (total > 0 && !opts.includes(total)) opts.push(total);
    if (total <= 0) opts = [10, 25, 50, 100, 250, 500];

    return Array.from(new Set(opts)).sort((a, b) => a - b);
  }, [total]);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  const startIndex = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndex = total === 0 ? 0 : Math.min(page * pageSize, total);

  const hasActiveFilters = Boolean(search.trim() || category || brand);

  function clearFilters() {
    setSearch("");
    setCategory("");
    setBrand("");
    setPage(1);
  }

  function onChangePageSize(value) {
    const nextPageSize = Number(value) || 10;
    setPageSize(nextPageSize);
    setPage(1);
  }

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const variantRows = useMemo(() => {
    return visibleProducts.flatMap((product) => {
      const variants = Array.isArray(product?.variants) ? product.variants : [];

      return variants.map((variant, index) => {
        const variantPrice = pickBestPrice(variant?.prices || []);
        const batch = getBatchSummary(variant);

        return {
          rowId: `${product.id}::${variant?.id || index}`,
          product,
          variant,
          image: getImageUrl(product),
          variantLabel: getVariantLabel(variant, index),
          sku: variant?.variantSku || product?.sku || "",
          barcode: product?.barcode || "",
          mrp: variantPrice?.mrp ?? variant?.mrp ?? product?.mrp ?? null,
          sellingPrice:
            variantPrice?.sellingPrice ??
            variantPrice?.selling_price ??
            variant?.sellingPrice ??
            product?.selling_price ??
            null,
          currency: variantPrice?.currency || product?.currency || "INR",
          availableQty: batch.totalAvailable || 0,
        };
      });
    });
  }, [visibleProducts]);

  const requestCount = requestItems.length;

  const requestTotalQty = useMemo(() => {
    return requestItems.reduce((sum, item) => sum + Number(item.qty || 0), 0);
  }, [requestItems]);

  return (
    <div className="space-y-6">
      <div className="rounded-[20px] border border-[#f2c6cc] bg-white p-6 shadow-[0_12px_30px_rgba(216,106,120,0.08)]">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-[14px] bg-[#fff6f7] text-[#d86a78]">
              <Package size={22} />
            </div>

            <div>
              <h2 className="warehouse-page-title">
                Warehouse Products
              </h2>
              {/* <p className="mt-2 max-w-2xl text-[14px] leading-6 text-slate-500">
                See only assigned products for this warehouse and send request
                to admin for required stock.
              </p> */}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={loadProducts}
              className="inline-flex items-center justify-center gap-2 rounded-[12px] border border-[#f2c6cc] px-4 py-2.5 text-[13px] font-semibold text-slate-600 transition hover:bg-[#fff8f9] hover:text-[#d86a78]"
            >
              <RefreshCcw size={16} />
              Refresh
            </button>

            <button
              type="button"
              onClick={() => setRequestOpen(true)}
              className="relative inline-flex items-center justify-center gap-2 rounded-[14px] bg-[#d86a78] px-4 py-2.5 text-[13px] font-semibold text-white transition hover:bg-[#c95b69]"
            >
              <ClipboardList size={16} />
              Request Cart
              {requestCount > 0 ? (
                <span className="ml-1 inline-flex min-w-[22px] items-center justify-center rounded-full bg-white px-1.5 py-0.5 text-[11px] font-bold text-[#d86a78]">
                  {requestCount}
                </span>
              ) : null}
            </button>
          </div>
        </div>

        <form onSubmit={handleSearchSubmit} className="mt-5 space-y-3">
          <div className="flex flex-col gap-3 lg:flex-row">
            <div className="relative flex-1">
              <Search
                size={18}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search by product name, SKU or barcode"
                className="h-[44px] w-full rounded-[12px] border border-[#f2c6cc] bg-white pl-11 pr-4 text-[14px] text-slate-700 outline-none transition focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
              />
            </div>

            <button
              type="submit"
              className="inline-flex h-[44px] items-center justify-center gap-2 rounded-[12px] bg-[#d86a78] px-5 text-[14px] font-semibold text-white transition hover:bg-[#c95b69]"
            >
              <Search size={16} />
              Search
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[2fr_1fr_auto]">
            <SelectLike
              value={category}
              onChange={(value) => {
                setPage(1);
                setCategory(value);
                          }}
              placeholder="Category"
              options={filterOptions.category}
              disabled={lookups.loading}
            />


            <SelectLike
              value={brand}
              onChange={(value) => {
                setPage(1);
                setBrand(value);
              }}
              placeholder="Brand"
              options={filterOptions.brand}
              disabled={lookups.loading}
            />

            <button
              type="button"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-[#f2c6cc] bg-white px-4 text-[13px] font-semibold text-[#d86a78] transition hover:bg-[#fff4f6] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <X size={15} />
              Clear Filters
            </button>
          </div>

          {lookups.error ? (
            <div className="rounded-[12px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-2 text-[12px] font-medium text-[#d86a78]">
              {lookups.error}
            </div>
          ) : null}
        </form>
      </div>

      {error ? (
        <div className="rounded-[16px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-3 text-[14px] font-medium text-[#d86a78]">
          {error}
        </div>
      ) : null}

      <div className="rounded-[20px] border border-[#f2c6cc] bg-white shadow-[0_12px_30px_rgba(216,106,120,0.08)]">
        <div className="flex flex-col gap-4 border-b border-[#f8dde1] px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-[17px] font-bold text-slate-800">
              Assigned Products
            </h3>

            {/* <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-[#fff3f5] px-3 py-1 text-[12px] font-bold text-[#d86a78]">
                Total Assigned: {total}
              </span>

              <span className="rounded-full bg-slate-100 px-3 py-1 text-[12px] font-semibold text-slate-600">
                Showing {startIndex}-{endIndex}
              </span>

              <span className="rounded-full bg-slate-100 px-3 py-1 text-[12px] font-semibold text-slate-600">
                Page {page} / {totalPages}
              </span>
            </div> */}
          </div>

          <div className="rounded-[16px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-3 text-right">
            <div className="text-[12px] font-semibold text-slate-500">
              Request Cart
            </div>
            <div className="mt-1 text-[14px] font-bold text-slate-800">
              {requestCount} products / {requestTotalQty} qty selected
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1380px]">
            <thead className="bg-[#fff7f8]">
              <tr className="text-left text-[13px] font-semibold text-slate-600">
                <th className="px-5 py-4">Product</th>
                <th className="px-5 py-4">SKU / Barcode</th>
                <th className="px-5 py-4">Category</th>
                <th className="px-5 py-4">Brand</th>
                <th className="px-5 py-4">UOM</th>
                <th className="px-5 py-4">Weight</th>
                <th className="px-5 py-4">Tax</th>
                <th className="px-5 py-4">MRP</th>
                <th className="px-5 py-4">Selling Price</th>
                <th className="px-5 py-4">Available Qty</th>
                <th className="px-5 py-4 text-right">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={11}
                    className="px-5 py-8 text-center text-[14px] text-slate-500"
                  >
                    Loading assigned products...
                  </td>
                </tr>
              ) : variantRows.length ? (
                variantRows.map((row) => {
                  const { product: item, variant, image } = row;

                  const isAdded = requestItems.some(
                    (reqItem) =>
                      reqItem.productId === item.id &&
                      String(reqItem.productVariantId || "") ===
                      String(variant?.id || ""),
                  );

                  return (
                    <tr
                      key={row.rowId}
                      className="border-t border-[#f8dde1] text-[14px] text-slate-700"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-12 w-12 overflow-hidden rounded-xl border border-[#f2c6cc] bg-[#fff7f8]">
                            {image ? (
                              <img
                                src={image}
                                alt={item.name || "product"}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-400">
                                No Img
                              </div>
                            )}
                          </div>

                          <div className="min-w-0">
                            <button
                              type="button"
                              onClick={() => goToProductDetails(item.id)}
                              title={item.name}
                              className="block max-w-[260px] truncate whitespace-nowrap text-left font-semibold text-slate-800 transition hover:text-[#d86a78] hover:underline"
                            >
                              {item.name || "-"}
                            </button>

                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <span className="max-w-[220px] truncate rounded-full bg-[#fff3f5] px-2.5 py-1 text-[11px] font-semibold text-[#d86a78]">
                                Variant: {row.variantLabel}
                              </span>

                              {variant?.isDefault ? (
                                <span className="rounded-full bg-[#eefbf3] px-2.5 py-1 text-[11px] font-semibold text-emerald-600">
                                  Default
                                </span>
                              ) : null}
                            </div>

                            {item.short_description ? (
                              <div className="mt-1 max-w-[280px] truncate text-[12px] text-slate-500">
                                {item.short_description}
                              </div>
                            ) : null}

                            {isAdded ? (
                              <div className="mt-1 text-[11px] font-semibold text-[#d86a78]">
                                Added in request cart
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="space-y-1">
                          <div>{row.sku || "-"}</div>
                          <div className="text-[12px] text-slate-500">
                            {row.barcode || "-"}
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">{item.category_name || "-"}</td>
                      <td className="px-5 py-4">{item.brand_name || "-"}</td>
                      <td className="px-5 py-4">
                        {item.unit_symbol || item.unit_name || "-"}
                      </td>
                      <td className="px-5 py-4">
                        {variant?.weightValue
                          ? `${variant.weightValue} ${variant.weightUnitSymbol || ""}`.trim()
                          : item.weight_value
                            ? `${item.weight_value} ${item.weight_unit_symbol || ""}`.trim()
                            : "-"}
                      </td>
                      <td className="px-5 py-4">
                        {item.tax_percent ? `${item.tax_percent}%` : "-"}
                      </td>
                      <td className="px-5 py-4">
                        {formatCurrency(row.mrp, row.currency)}
                      </td>
                      <td className="px-5 py-4">
                        {formatCurrency(row.sellingPrice, row.currency)}
                      </td>
                      <td className="px-5 py-4">{row.availableQty || 0}</td>

                      <td className="px-5 py-4">
                        <div className="flex justify-end">
                          {isAdded ? (
                            <button
                              type="button"
                              onClick={() => removeRequestItem(item.id, variant?.id || "")}
                              className="inline-flex items-center gap-2 rounded-[10px] border border-[#f2c6cc] bg-white px-3 py-2 text-[12px] font-semibold text-[#d86a78] transition hover:bg-[#fff4f6]"
                              title="Remove from request cart"
                            >
                              <X size={14} />
                              Remove
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => addProductToRequest(item, variant)}
                              className="inline-flex items-center gap-2 rounded-[10px] bg-[#d86a78] px-3 py-2 text-[12px] font-semibold text-white transition hover:bg-[#c95b69]"
                            >
                              <Plus size={14} />
                              Request
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={11}
                    className="px-5 py-8 text-center text-[14px] text-slate-500"
                  >
                    No assigned products found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-[#f8dde1] px-5 py-4 text-[12px] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-center font-medium sm:text-left">
            Showing {startIndex}-{endIndex} of {total}
          </div>

          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <div className="flex items-center justify-between gap-3 sm:justify-start">
              <span>Rows</span>

              <select
                value={pageSize}
                disabled={loading}
                onChange={(e) => onChangePageSize(e.target.value)}
                className="h-9 min-w-[88px] rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[12px] text-slate-600 outline-none focus:border-[#d86a78] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {pageSizeOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-between gap-2 sm:justify-start">
              <button
                type="button"
                onClick={() => canPrev && setPage((p) => p - 1)}
                disabled={!canPrev || loading}
                className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Prev
              </button>

              <span className="min-w-[86px] text-center font-medium">
                Page {page} / {totalPages}
              </span>

              <button
                type="button"
                onClick={() => canNext && setPage((p) => p + 1)}
                disabled={!canNext || loading}
                className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {requestOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-[24px] bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#f8dde1] px-6 py-4">
              <div>
                <h3 className="text-[18px] font-bold text-slate-800">
                  Request Cart
                </h3>
                <p className="mt-1 text-[13px] text-slate-500">
                  {requestCount} products added · {requestTotalQty} total qty
                </p>
              </div>

              <button
                type="button"
                onClick={() => setRequestOpen(false)}
                className="rounded-[10px] p-2 text-slate-500 hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
              {submitMessage ? (
                <div className="mb-4 rounded-[14px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-3 text-[13px] font-medium text-[#d86a78]">
                  {submitMessage}
                </div>
              ) : null}

              <div className="space-y-4">
                {requestItems.length ? (
                  requestItems.map((item) => {
                    const rowKey = `${item.productId}::${item.productVariantId || ""}`;
                    return (
                      <div
                        key={rowKey}
                        className="rounded-[16px] border border-[#f2c6cc] bg-[#fffdfd] p-4"
                      >
                        <div className="grid gap-4 lg:grid-cols-[82px_1fr_280px]">
                          <div className="h-20 w-20 overflow-hidden rounded-xl border border-[#f2c6cc] bg-[#fff7f8]">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-[11px] text-slate-400">
                                No Img
                              </div>
                            )}
                          </div>

                          <div className="min-w-0">
                            <div className="text-[15px] font-semibold text-slate-800">
                              {item.name}
                            </div>

                            <div className="mt-2 grid gap-2 text-[12px] text-slate-500 sm:grid-cols-2 xl:grid-cols-3">
                              <div>SKU: {item.sku || "-"}</div>
                              <div>Barcode: {item.barcode || "-"}</div>
                              <div>Category: {item.category || "-"}</div>
                              <div>Brand: {item.brand || "-"}</div>
                              <div>UOM: {item.uom || "-"}</div>
                              <div>
                                Variant:{" "}
                                {item.variantLabel ||
                                  item.variants?.find(
                                    (v) => String(v.id) === String(item.productVariantId)
                                  )?.label ||
                                  "Default"}
                              </div>
                              <div>
                                MRP: {formatCurrency(item.mrp, item.currency)}
                              </div>
                              <div>
                                Selling: {formatCurrency(item.sellingPrice, item.currency)}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col gap-3 lg:items-stretch">
                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={() =>
                                  removeRequestItem(item.productId, item.productVariantId)
                                }
                                className="inline-flex h-[40px] items-center gap-2 rounded-[12px] border border-[#f2c6cc] px-4 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"
                              >
                                <X size={14} />
                                Remove
                              </button>
                            </div>

                            <div>
                              <label className="mb-1 block text-[12px] font-semibold text-slate-500">
                                Variant
                              </label>
                              <select
                                value={item.productVariantId || ""}
                                onChange={(e) =>
                                  updateItemVariant(
                                    item.productId,
                                    item.productVariantId,
                                    e.target.value
                                  )
                                }
                                className="h-[42px] w-full rounded-[12px] border border-[#f2c6cc] px-3 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                              >
                                {(item.variants || []).map((variant) => (
                                  <option
                                    key={variant.id || variant.label}
                                    value={variant.id || ""}
                                  >
                                    {variant.label ||
                                      variant.variantName ||
                                      variant.name ||
                                      "Default"}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="mb-1 block text-[12px] font-semibold text-slate-500">
                                Requested Qty
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={item.qty}
                                onChange={(e) =>
                                  updateQty(
                                    item.productId,
                                    item.productVariantId,
                                    e.target.value
                                  )
                                }
                                className="h-[42px] w-full rounded-[12px] border border-[#f2c6cc] px-3 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="mt-4">
                          <label className="mb-2 block text-[12px] font-semibold text-slate-500">
                            Item Note
                          </label>
                          <input
                            type="text"
                            value={item.itemNote || ""}
                            onChange={(e) =>
                              updateItemNote(
                                item.productId,
                                item.productVariantId,
                                e.target.value,
                              )
                            }
                            placeholder="Optional note for this item"
                            className="h-[42px] w-full max-w-[140px] rounded-[12px] border border-[#f2c6cc] px-3 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="rounded-[16px] border border-dashed border-[#f2c6cc] bg-[#fffafb] px-4 py-8 text-center text-[14px] text-slate-500">
                    No products added yet. Click Request from product list.
                  </div>
                )}
              </div>

              <div className="mt-5">
                <label className="mb-2 block text-[13px] font-semibold text-slate-600">
                  Notes for Admin
                </label>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional overall note for admin"
                  className="w-full rounded-[14px] border border-[#f2c6cc] px-4 py-3 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-[#f8dde1] px-6 py-4">
              <button
                type="button"
                onClick={() => setRequestOpen(false)}
                className="rounded-[12px] border border-[#f2c6cc] px-5 py-2.5 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={submitOrderRequest}
                className="rounded-[12px] bg-[#d86a78] px-5 py-2.5 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send Request"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
